/**
 * utilities/localization.ts
 * desc: utility functions and helper methods related to localization feature
 * note: implementation based on formatjs lib
 */
import { createIntl, createIntlCache, type IntlShape, type MessageFormatElement } from 'react-intl';
import constants from '../settings/constants';
import { getQueryStringObject } from './index';
// @sentry/react re-exports the full browser API — @sentry/browser itself is
// not a declared dependency (it only resolves through hoisting)
import * as Sentry from '@sentry/react';

// compiled formatjs dictionary shape (`formatjs compile-folder --ast`)
export type CompiledMessages = Record<string, MessageFormatElement[]>;

// react-intl provider instance
let intlProvider: IntlShape | null = null;
const defaultMessages: Record<string, string | undefined> = {};

/**
 * translate
 * desc: Simple function that returns a FormattedMessage result from react-intl lib.
 * notes: the main purpose of this function is to make code more readable
 * @param {String}    key
 * @param {Object}    data
 * @return {String | boolean} FormattedMessage result or false
 */
export function translate(key: string, data?: Record<string, unknown>): string { // move to localization
    if (!key) return `[TRANSLATE ERROR]: Translation key is not provided.`;
    if (!intlProvider) return `[TRANSLATE ERROR]: Intl Provider is not set.`;
    // values pass straight through to intl-messageformat; the cast only
    // satisfies the string overload
    let result = intlProvider.formatMessage({ id: key, defaultMessage: defaultMessages[key] }, data as Record<string, string>);
    const qsObj = getQueryStringObject();

    if (qsObj.locale === 'test') result = 'xxx';
    else if (result === key) result = `[TRANSLATE ERROR]: cannot find translation key:"${key}" for locale:"${intlProvider.locale}"`;

    return result;
}

/**
 * updateIntlProvider
 * desc: creates react-intl provider instance
 * notes: we use the imperative approach here to simplify usage of translate function and make it possible to use outside of react lifecycle
 * @param {String}    locale
 * @param {Object}    messages
 * @return {boolean}
 */
export function updateIntlProvider(locale: string, messages: CompiledMessages): boolean {
    try {
        const cache = createIntlCache();
        const defaultLocale = window.Ym?.DEFAULT_LOCALE ? window.Ym.DEFAULT_LOCALE : constants.LOCALES.DEFAULT;
        intlProvider = createIntl({
            locale: locale,
            messages,
            defaultLocale: defaultLocale,
            onError: (err) => {
                console.error(err);
                // @ts-expect-error captureMessage is typed for strings, but the
                // SDK normalizes any value
                Sentry.captureMessage(err);
            }
        }, cache);

        if (defaultLocale === locale) {
            for (const [ key, value ] of Object.entries(messages)) {
                // only literal AST elements carry `value`; other kinds read undefined
                defaultMessages[key] = (value[0] as { value?: string } | undefined)?.value;
            }
        }
        return true;
    } catch (err) {
        console.error(err);
        return false;
    }
}

/**
 * loadDictionaries
 * desc: builds a dictionary for every supported locale
 * notes: uses app-settings.json as the source of truth
 * @param {String}    locale
 * @return {Promise}  Promise object represents the array of dictionaries splitted by locale parts
 */
export async function loadDictionaries(locale: string): Promise<{ locale: string; messages: CompiledMessages }> {
    if (locale === 'test') locale = 'en'; // special case exception
    locale = locale.replace('_', '-');
    const localeSplit = locale.split('-');

    // determine locale files
    const languageFile = localeSplit[0];
    const countryFile = (localeSplit[1]) ? `${localeSplit[0]}-${localeSplit[1]}` : null;
    const dialectFile = (localeSplit.length === 3) ? locale : null;

    // require lanaguage file
    // note: .default is unwrapped explicitly — the dictionary keys are dotted
    //       strings, which JSON modules do not expose as named exports
    let languageText: CompiledMessages = {};
    try { languageText = (await import(`../locales/compiled/${languageFile}.json`)).default; }
    catch {
        console.error('Can\'t resolve compiled language file.');
    }

    // require country file
    let countryText: CompiledMessages = {};
    if (countryFile) {
        try { countryText = (await import(`../locales/compiled/${countryFile}.json`)).default; }
        catch {
            console.error('Can\'t resolve compiled country file.');
        }
    }

    // require dialect file
    let dialectText: CompiledMessages = {};
    if (dialectFile) {
        try { dialectText = (await import(`../locales/compiled/${dialectFile}.json`)).default; }
        catch {
            console.error('Can\'t resolve compiled dialect file.');
        }
    }

    // create locale dictionary
    const dictionary = { ...languageText, ...countryText, ...dialectText };

    return { locale, messages: dictionary };
}

/**
 * mergeDictionaries
 * desc: Merge dictionaries splitted by locale parts into single object
 * notes: intended to use with loadDictionaries function
 * @param {Array}    dictionaries
 * @return {Object}  dictionarie
 */
export function mergeDictionaries(dictionaries: CompiledMessages[]): CompiledMessages {
    // spreading a missing entry is a no-op, so no per-entry fallbacks needed
    return { ...dictionaries[0], ...dictionaries[1], ...dictionaries[2] };
}

/**
 * setBodyClasses
 * desc: set body classes
 * @param {String}  locale
 */
export function setBodyClasses(locale: string, prevLocale: string | false = false): void {
    if (!locale) return;

    const bodyEle = document.getElementsByTagName('body')[0];
    const newLocaleParts = _getBodyLocaleParts(locale);
    const prevLocaleParts = _getBodyLocaleParts(prevLocale);

    if (prevLocaleParts.langClass) bodyEle.classList.remove(prevLocaleParts.langClass);
    if (prevLocaleParts.countryClass) bodyEle.classList.remove(prevLocaleParts.countryClass);
    if (prevLocaleParts.dialectClass) bodyEle.classList.remove(prevLocaleParts.dialectClass);

    // add body classes
    // note: only add dialect if necessary
    // (langClass is always set when a locale was passed — guarded above)
    bodyEle.classList.add(newLocaleParts.langClass!);
    if (newLocaleParts.countryClass) {
        bodyEle.classList.add(newLocaleParts.countryClass);
    }
    if (newLocaleParts.dialectClass) {
        bodyEle.classList.add(newLocaleParts.dialectClass);
    }
}

/**
 * setIntlProvider
 * desc: manually set int provider instance
 * @param {object}  provider
 */
export function setIntlProvider(provider: IntlShape): void {
    intlProvider = provider;
}

/**
 * getIntlProvider
 * desc: read access to the module-held provider instance
 * note: a getter instead of exporting the mutable let-binding (Sonar S6861);
 *       reads happen at call time, identical to the old live binding
 */
export function getIntlProvider(): IntlShape | null {
    return intlProvider;
}

function _getBodyLocaleParts(locale: string | false): { langClass?: string; countryClass?: string; dialectClass?: string } {
    if (!locale) return {};
    const localeSplit = locale.split('-');

    // determine body classes
    const langClass = `lang-${localeSplit[0]}`;
    const countryClass = localeSplit[1] ? `country-${localeSplit[1]}` : '';
    const dialectClass = (localeSplit.length === 3) ? `dialect-${localeSplit[2]}` : '';

    return { langClass, countryClass, dialectClass };
}
